import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '../Breadcrumb/index';
import { Card, CardHeader, CardContent, CardDescription } from '../Card/index';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Separator } from '@art-of-hospitality-solutions-llc/ui-kit/components/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../Tabs/index';
import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import service from '@Service';

const SubscriptionTierDetails = () => {
  const SUBSCRIPTION_TYPE_MAP = {
    STORK_CLUB_MEMBERSHIP: 'Stork Club Membership',
    BOTTLE_SERVICE: 'Bottle Service',
  };

  const SUBSCRIPTION_UNIT_MAP = {
    YEAR: 'Year',
    MONTH: 'Month',
    LIFETIME: 'Lifetime',
    DAY: 'Day',
  };

  const { id } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [tierDetails, setTierDetails] = useState(null);

  const fetchTierDetails = async () => {
    const data = await service.getSubscriptionTierById(id);
    if (data) {
      setTierDetails(data);
    }
  };

  const formatSubscriptionType = () => {
    let formatted = 'N/A';
    if (tierDetails?.subscriptionType) {
      formatted = SUBSCRIPTION_TYPE_MAP[tierDetails.subscriptionType];
    }
    return formatted;
  };

  const formatSubscriptionUnit = (unit: string) => {
    let formatted = 'N/A';
    if (unit) {
      formatted = SUBSCRIPTION_UNIT_MAP[unit];
    }
    return formatted;
  };

  const formatPrice = (price: string) => {
    const decimal = new Decimal(price)?.toNumber();
    const formatted = decimal.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    return formatted;
  };

  useEffect(() => {
    if (!tierDetails && id) {
      fetchTierDetails();
    }
  }, [tierDetails, id]);

  return (
    <div className="flex h-screen w-full">
      <div className="flex w-2/3 min-w-170 flex-col pt-12">
        <div className="font-questrial p-4">
          <div className="flex items-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem
                  onClick={() => {
                    navigate('/');
                  }}
                  className={`cursor-pointer ${pathname !== '/dashboard' && 'opacity-50'}`}
                >
                  Dashboard
                </BreadcrumbItem>
                <BreadcrumbSeparator></BreadcrumbSeparator>
                <BreadcrumbItem
                  onClick={() => {
                    navigate(`/subscription-tiers`);
                  }}
                  className={`cursor-pointer ${pathname !== '/subscription-tiers' && 'opacity-50'}`}
                >
                  Subscription Tiers
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </div>

        <Card className="p-8">
          <CardHeader>
            <Heading level={'h5'}>{tierDetails?.title}</Heading>
            <CardDescription>
              {tierDetails?.description || 'No description provided.'}
            </CardDescription>
            <Separator className="bg-[#caab6a]" />
          </CardHeader>

          <CardContent className="mx-auto min-h-100 w-full">
            <Tabs defaultValue="basic-details">
              <TabsList className="mx-auto">
                <TabsTrigger
                  className="font-questrial rounded-none border-b-4 data-[state=active]:border-black data-[state=inactive]:border-transparent"
                  value="basic-details"
                >
                  Basic Details
                </TabsTrigger>

                <TabsTrigger
                  className="font-questrial rounded-none border-b-4 data-[state=active]:border-black data-[state=inactive]:border-transparent"
                  value="subscription-details"
                >
                  Subscription Details
                </TabsTrigger>

                <TabsTrigger
                  className="font-questrial rounded-none border-b-4 data-[state=active]:border-black data-[state=inactive]:border-transparent"
                  value="wallet-details"
                >
                  Wallet Details
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic-details">
                <div className="mt-4 flex min-h-75 rounded-md bg-gray-100 p-8">
                  <div className="flex w-full flex-col space-y-4">
                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Title:</div>
                      <div>{tierDetails?.title || 'N/A'}</div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Description:</div>
                      <div>{tierDetails?.description || 'N/A'}</div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Created by:</div>
                      <div>{tierDetails?.createdByUser?.name || 'N/A'}</div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Created at:</div>
                      <div>
                        {tierDetails?.createdAt
                          ? DateTime.fromISO(tierDetails?.createdAt)
                              .toLocal()
                              .toFormat('MMM dd, yyyy')
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Last updated at:</div>
                      <div>
                        {tierDetails?.updatedAt
                          ? DateTime.fromISO(tierDetails?.updatedAt)
                              .toLocal()
                              .toFormat('MMM dd, yyyy')
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Last updated by:</div>
                      <div>{tierDetails?.updatedByUser?.name || 'N/A'}</div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="subscription-details">
                <div className="mt-4 flex rounded-md bg-gray-100 p-8">
                  <div className="flex w-full flex-col space-y-4">
                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Subscription type:</div>
                      <div>{formatSubscriptionType()}</div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Subscription unit:</div>
                      <div>
                        {tierDetails?.subscriptionUnit
                          ? formatSubscriptionUnit(tierDetails.subscriptionUnit)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Subscription duration:
                      </div>
                      <div>
                        {tierDetails?.subscriptionUnit &&
                        tierDetails?.subscriptionDuration
                          ? tierDetails?.subscriptionDuration +
                            ' ' +
                            formatSubscriptionUnit(
                              tierDetails?.subscriptionUnit,
                            )
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Initial subscription price:
                      </div>
                      <div>
                        {tierDetails?.initialSubscriptionPrice
                          ? formatPrice(tierDetails.initialSubscriptionPrice)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Subsequent subscription price:
                      </div>
                      <div>
                        {tierDetails?.subsequentSubscriptionPrice
                          ? formatPrice(tierDetails.subsequentSubscriptionPrice)
                          : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="wallet-details">
                <div className="mt-4 flex rounded-md bg-gray-100 p-8">
                  <div className="flex w-full flex-col space-y-4">
                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Wallet unit:</div>
                      <div>
                        {tierDetails?.walletUnit
                          ? formatSubscriptionUnit(tierDetails.walletUnit)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Wallet duration:</div>
                      <div>
                        {tierDetails?.walletUnit && tierDetails?.walletDuration
                          ? tierDetails?.walletDuration +
                            ' ' +
                            formatSubscriptionUnit(tierDetails?.walletUnit)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">Initial wallet price:</div>
                      <div>
                        {tierDetails?.initialWalletPrice
                          ? formatPrice(tierDetails.initialWalletPrice)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Subsequent wallet price:
                      </div>
                      <div>
                        {tierDetails?.subsequentWalletPrice
                          ? formatPrice(tierDetails.subsequentWalletPrice)
                          : 'N/A'}
                      </div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Wallet rollover duration amount:
                      </div>
                      <div>{tierDetails?.walletRolloverDurationAmount}</div>
                    </div>

                    <div className="flex flex-row justify-between">
                      <div className="font-semibold">
                        Wallet rollover duration unit:
                      </div>
                      <div>
                        {formatSubscriptionUnit(
                          tierDetails?.walletRolloverDurationUnit,
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
          <Separator className="bg-[#caab6a]" />
        </Card>
      </div>
    </div>
  );
};

export default SubscriptionTierDetails;
